import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from './Modal';

/**
 * 관리자 콘솔 공지 예약 수정 모달 (본문 + 시각).
 *
 * 채팅의 예약 전송·수정은 ScheduleComposerModal(작성·첨부·목록을 한 창에서)로 옮겼고, 이 모달은
 * AdminConsole의 공지 예약 수정만 쓴다. 공지 예약은 텍스트 전용이라 첨부 UI가 없다.
 *
 * 시각 입력은 날짜 점프와 같은 네이티브 <input type="date">/<input type="time"> —
 * 서버가 초를 내림하므로 분 단위다. 범위 검증(1분 뒤~30일 안)은 서버가 최종 판정하고
 * 여기서는 같은 규칙으로 사전 차단해 인라인 오류로 보여준다.
 */
interface ScheduleMessageModalProps {
  isOpen: boolean;
  /** 예전 호출부 호환 — 수정 모드만 남았다 */
  mode?: 'edit';
  initialText: string;
  /** 기존 예약 시각 (ISO) */
  initialScheduledAt?: string | null;
  /** false를 돌려주면 실패 — 모달을 닫지 않고 그대로 재시도할 수 있게 둔다 */
  onConfirm: (text: string, scheduledAtIso: string) => Promise<boolean>;
  onClose: () => void;
}

const pad = (n: number) => String(n).padStart(2, '0');
const toDateStr = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toTimeStr = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const MAX_AHEAD_MS = 30 * 24 * 60 * 60 * 1000;

export const ScheduleMessageModal: React.FC<ScheduleMessageModalProps> = ({
  isOpen,
  initialText,
  initialScheduledAt,
  onConfirm,
  onClose,
}) => {
  const [text, setText] = useState('');
  const [dateStr, setDateStr] = useState('');
  const [timeStr, setTimeStr] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // 열릴 때마다 초기화 — 기존 예약 시각(없으면 1시간 뒤, 분 내림)
  useEffect(() => {
    if (!isOpen) return;
    setText(initialText);
    setError(null);
    setSubmitting(false);
    const base = initialScheduledAt
      ? new Date(initialScheduledAt)
      : new Date(Date.now() + 60 * 60 * 1000);
    base.setSeconds(0, 0);
    setDateStr(toDateStr(base));
    setTimeStr(toTimeStr(base));
  }, [isOpen, initialText, initialScheduledAt]);

  // 열릴 때마다 재계산 — isOpen이 유일한 의도적 dep (모달이 며칠 열려 있을 일은 없다)
  const { minDate, maxDate } = useMemo(() => {
    const now = new Date();
    return { minDate: toDateStr(now), maxDate: toDateStr(new Date(now.getTime() + MAX_AHEAD_MS)) };
  }, [isOpen]);

  const handleConfirm = async () => {
    const trimmed = text.trim();
    if (!trimmed) {
      setError('메시지 내용을 입력해주세요.');
      return;
    }
    const [y, m, d] = dateStr.split('-').map(Number);
    const [hh, mm] = timeStr.split(':').map(Number);
    if (!y || !m || !d || Number.isNaN(hh) || Number.isNaN(mm)) {
      setError('예약 시간 형식이 올바르지 않습니다.');
      return;
    }
    const when = new Date(y, m - 1, d, hh, mm, 0, 0);
    const now = Date.now();
    if (when.getTime() < now + 55 * 1000) {
      setError('예약 시간은 1분 뒤부터 선택할 수 있습니다.');
      return;
    }
    if (when.getTime() > now + MAX_AHEAD_MS) {
      setError('예약 시간은 30일 안까지 선택할 수 있습니다.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const ok = await onConfirm(trimmed, when.toISOString());
      if (!ok) setSubmitting(false); // 실패 — 모달 유지, 재시도 가능 (오류 토스트는 호출부)
    } catch {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="예약 수정"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg text-slate-500 hover:bg-slate-100 transition-colors"
          >
            취소
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            className="px-4 py-2 bg-brand-600 text-white text-sm font-semibold rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? '저장 중…' : '저장'}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          className="w-full px-3 py-2.5 rounded-lg bg-white border border-slate-200 text-sm text-slate-700 resize-none focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
        />

        <div>
          <p className="text-xs font-semibold text-slate-600 mb-1.5">보낼 시간</p>
          <div className="flex gap-2">
            <input
              type="date"
              value={dateStr}
              min={minDate}
              max={maxDate}
              onChange={(e) => setDateStr(e.target.value)}
              className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
            />
            <input
              type="time"
              step={60}
              value={timeStr}
              onChange={(e) => setTimeStr(e.target.value)}
              className="w-32 px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
            />
          </div>
          <p className="mt-1.5 text-xs text-slate-400">1분 뒤부터 30일 안까지 선택할 수 있습니다</p>
        </div>

        {error && <p className="text-xs text-brand-700">{error}</p>}
      </div>
    </Modal>
  );
};
